import { useCallback } from "react";

// US State to two-letter abbreviation mapping
const US_STATE_ABBREVIATIONS: { [key: string]: string } = {
  'alabama': 'AL',
  'alaska': 'AK',
  'arizona': 'AZ',
  'arkansas': 'AR',
  'california': 'CA',
  'colorado': 'CO',
  'connecticut': 'CT',
  'delaware': 'DE',
  'florida': 'FL',
  'georgia': 'GA',
  'hawaii': 'HI',
  'idaho': 'ID',
  'illinois': 'IL',
  'indiana': 'IN',
  'iowa': 'IA',
  'kansas': 'KS',
  'kentucky': 'KY',
  'louisiana': 'LA',
  'maine': 'ME',
  'maryland': 'MD',
  'massachusetts': 'MA',
  'michigan': 'MI',
  'minnesota': 'MN',
  'mississippi': 'MS',
  'missouri': 'MO',
  'montana': 'MT',
  'nebraska': 'NE',
  'nevada': 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  'ohio': 'OH',
  'oklahoma': 'OK',
  'oregon': 'OR',
  'pennsylvania': 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  'tennessee': 'TN',
  'texas': 'TX',
  'utah': 'UT',
  'vermont': 'VT',
  'virginia': 'VA',
  'washington': 'WA',
  'west virginia': 'WV',
  'wisconsin': 'WI',
  'wyoming': 'WY',
  'district of columbia': 'DC',
  'washington dc': 'DC',
  'puerto rico': 'PR',
  'guam': 'GU',
  'american samoa': 'AS',
  'northern mariana islands': 'MP',
  'u.s. virgin islands': 'VI',

  // 🇨🇦 Canadian Provinces & Territories
  'alberta': 'AB',
  'british columbia': 'BC',
  'manitoba': 'MB',
  'new brunswick': 'NB',
  'newfoundland and labrador': 'NL',
  'nova scotia': 'NS',
  'northwest territories': 'NT',
  'nunavut': 'NU',
  'ontario': 'ON',
  'prince edward island': 'PE',
  'quebec': 'QC',
  'saskatchewan': 'SK',
  'yukon': 'YT'
};

// Country to three-letter ISO code mapping (ISO 3166-1 alpha-3)
const COUNTRY_ABBREVIATIONS: { [key: string]: string } = {
  // North America
  'united states': 'USA',
  'united states of america': 'USA',
  'us': 'USA',
  'usa': 'USA',
  'canada': 'CAN',
  'ca': 'CAN',
  'mexico': 'MEX',
  'mx': 'MEX',
  
  // Europe
  'united kingdom': 'GBR',
  'great britain': 'GBR',
  'britain': 'GBR',
  'england': 'GBR',
  'scotland': 'GBR',
  'wales': 'GBR',
  'uk': 'GBR',
  'germany': 'DEU',
  'deutschland': 'DEU',
  'france': 'FRA',
  'italy': 'ITA',
  'spain': 'ESP',
  'espana': 'ESP',
  'netherlands': 'NLD',
  'holland': 'NLD',
  'belgium': 'BEL',
  'switzerland': 'CHE',
  'swiss': 'CHE',
  'austria': 'AUT',
  'portugal': 'PRT',
  'sweden': 'SWE',
  'norway': 'NOR',
  'denmark': 'DNK',
  'finland': 'FIN',
  'ireland': 'IRL',
  'poland': 'POL',
  'greece': 'GRC',
  'czech republic': 'CZE',
  'czech': 'CZE',
  'hungary': 'HUN',
  'romania': 'ROU',
  
  // Asia
  'china': 'CHN',
  'japan': 'JPN',
  'south korea': 'KOR',
  'korea': 'KOR',
  'india': 'IND',
  'australia': 'AUS',
  'new zealand': 'NZL',
  'singapore': 'SGP',
  'malaysia': 'MYS',
  'thailand': 'THA',
  'vietnam': 'VNM',
  'indonesia': 'IDN',
  'philippines': 'PHL',
  'pakistan': 'PAK',
  'bangladesh': 'BGD',
  'sri lanka': 'LKA',
  
  // South America
  'brazil': 'BRA',
  'argentina': 'ARG',
  'chile': 'CHL',
  'colombia': 'COL',
  'peru': 'PER',
  'venezuela': 'VEN',
  
  // Africa
  'south africa': 'ZAF',
  'egypt': 'EGY',
  'nigeria': 'NGA',
  'kenya': 'KEN',
  'ethiopia': 'ETH',
  'morocco': 'MAR',
  
  // Middle East
  'saudi arabia': 'SAU',
  'united arab emirates': 'ARE',
  'uae': 'ARE',
  'israel': 'ISR',
  'turkey': 'TUR',
  'turkiye': 'TUR'
};

export const normalizeValue = (
  value: string,
  abbreviationMap: Record<string, string>,
  abbreviationPattern: RegExp
): string => {
  if (!value) return '';

  const trimmed = value.trim();

  if (abbreviationPattern.test(trimmed)) {
    return trimmed;
  }

  const lower = trimmed.toLowerCase();

  return abbreviationMap[lower] || trimmed;
};

export const normalizeState = (state: string): string =>
  normalizeValue(state, US_STATE_ABBREVIATIONS, /^[A-Z]{2}$/);

export const normalizeCountry = (country: string): string =>
  normalizeValue(country, COUNTRY_ABBREVIATIONS, /^[A-Z]{3}$/);
